import React from 'react';
import {
  Box,
  Grid,
  Heading,
  Text,
  VStack,
  HStack,
  Icon,
  Skeleton,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  Link,
  Alert,
  AlertIcon,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  useToast,
} from '@chakra-ui/react';
import { FiPercent, FiTrendingUp, FiRepeat, FiDollarSign } from 'react-icons/fi';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { format, parseISO, isValid } from 'date-fns';
import { api, apiErrorMessage, ClaimBalance, CrossChainClaim } from '../../api/client';
import { generateCompleteChartData } from '../../utils/chartUtils';
import {
  fmtNum,
  fmtUsd,
  fmtStable,
  stableAmount,
  fmtAssetOnChain,
  shorten,
} from '../../utils/format';

const BRAND = '#004dff';
const GRID = '#eef2f7';

// Matches the backend's page size for orders and claims
const PAGE_SIZE = 25;

const fmtDate = (value: string | null | undefined): string => {
  if (!value) return '';
  const date = parseISO(value);
  return isValid(date) ? format(date, 'd MMM yyyy') : '';
};

// Shown in place of a section's data: why it's empty, missing or failed
const Notice: React.FC<{ error?: boolean; children: React.ReactNode }> = ({ error, children }) => (
  <Text fontSize="sm" color={error ? 'red.500' : 'gray.500'}>{children}</Text>
);

// Previous/Next for the paginated tables, which the backend serves PAGE_SIZE rows at a time
const Pager: React.FC<{ offset: number; nextOffset?: number | null; onChange: (offset: number) => void }> = ({
  offset,
  nextOffset,
  onChange,
}) =>
  offset > 0 || nextOffset != null ? (
    <HStack justify="flex-end" mt={3}>
      <Button size="sm" variant="outline" isDisabled={offset === 0} onClick={() => onChange(Math.max(0, offset - PAGE_SIZE))}>
        Previous
      </Button>
      <Button size="sm" variant="outline" isDisabled={nextOffset == null} onClick={() => nextOffset != null && onChange(nextOffset)}>
        Next
      </Button>
    </HStack>
  ) : null;

const CLAIM_STATUS_COLORS: Record<CrossChainClaim['status'], string> = {
  processing: 'yellow',
  completed: 'green',
  failed: 'red',
};

// USDC/USDT fees for Breez SDK - Spark: the partner's Flashnet
// Orchestra affiliate earnings, which they claim from here.
const CrossChainFees: React.FC<{ startDate: string; endDate: string }> = ({ startDate, endDate }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const toast = useToast();
  const queryClient = useQueryClient();

  const [ordersOffset, setOrdersOffset] = React.useState(0);
  const [claimsOffset, setClaimsOffset] = React.useState(0);
  const [claiming, setClaiming] = React.useState<ClaimBalance | null>(null);
  const cancelRef = React.useRef<HTMLButtonElement>(null);

  const feesQuery = useQuery({
    queryKey: ['cross-chain-fees'],
    queryFn: async () => (await api.crossChain.getFees()).data,
    retry: false,
  });
  const unavailable = (feesQuery.error as any)?.response?.status === 503;

  const statsQuery = useQuery({
    queryKey: ['cross-chain-stats', startDate, endDate],
    queryFn: async () => (await api.crossChain.getStats(startDate, endDate)).data,
    enabled: feesQuery.isSuccess,
  });

  const claimsQuery = useQuery({
    queryKey: ['cross-chain-claims', claimsOffset],
    queryFn: async () => (await api.crossChain.getClaims(claimsOffset)).data,
    enabled: feesQuery.isSuccess,
  });

  const ordersQuery = useQuery({
    queryKey: ['cross-chain-orders', ordersOffset],
    queryFn: async () => (await api.crossChain.getOrders(ordersOffset)).data,
    enabled: feesQuery.isSuccess,
  });

  // A claim, or one whose outcome is unknown, changes balances and claims. A
  // refused claim can mean the fee was turned off elsewhere, so reload it too.
  const reloadClaims = () => {
    for (const key of ['cross-chain-fees', 'cross-chain-stats', 'cross-chain-claims']) {
      queryClient.invalidateQueries({ queryKey: [key] });
    }
  };

  const claimMutation = useMutation({
    mutationFn: (balance: ClaimBalance) => api.crossChain.claim(balance),
    onSuccess: () => {
      reloadClaims();
      toast({
        title: 'Claim submitted',
        description: 'The payout is being processed. Its status is shown under Claims.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    },
    onError: (error: any) => {
      reloadClaims();
      toast({
        title: 'Error',
        description: apiErrorMessage(error, 'Failed to claim'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
    onSettled: () => setClaiming(null),
  });

  const metrics = statsQuery.data?.metrics;
  const balances = statsQuery.data?.balances || [];
  const totals = statsQuery.data?.totals;
  const claims = claimsQuery.data?.claims || [];
  const orders = ordersQuery.data?.orders || [];

  // Earnings summed per month, in USD (both settlement assets are USD stablecoins)
  const chartData = React.useMemo(() => {
    const byMonth = new Map<string, number>();
    for (const bucket of metrics?.series || []) {
      const month = bucket.date.slice(0, 7);
      byMonth.set(month, (byMonth.get(month) || 0) + stableAmount(bucket.earnedAmount));
    }
    const monthly = Array.from(byMonth, ([month, fees]) => ({ date: `${month}-01`, fees }));
    return generateCompleteChartData(monthly, startDate, endDate);
  }, [metrics, startDate, endDate]);

  if (unavailable) {
    return (
      <Box p={6} bg={cardBg} rounded="xl" border="1px" borderColor={borderColor}>
        <Text color="gray.600">USDC/USDT fees are not available yet.</Text>
      </Box>
    );
  }

  if (feesQuery.isError) {
    return (
      <Box p={6} bg={cardBg} rounded="xl" border="1px" borderColor={borderColor}>
        <Text color="gray.600">{apiErrorMessage(feesQuery.error, 'Failed to load USDC/USDT fees')}</Text>
      </Box>
    );
  }

  const fees = feesQuery.data;
  const statsLoading = feesQuery.isLoading || statsQuery.isLoading;
  const statsError = statsQuery.isError ? apiErrorMessage(statsQuery.error, 'Failed to load USDC/USDT fee stats') : null;
  // Orchestra doesn't report on a turned-off fee. Show that rather than zeros.
  const statsHidden = statsQuery.data?.available === false;
  const statsMissing = !!statsError || statsHidden;
  const orNoData = (value: string) => (statsMissing ? '–' : value);
  // Orchestra has no data for a turned-off fee; for one that's on it's unexpected
  const unavailableWhen = fees?.enabled ? 'right now' : 'while the USDC/USDT fee is turned off';

  if (fees && !fees.enabled && !fees.payoutAddress) {
    return (
      <Box p={6} bg={cardBg} rounded="xl" border="1px" borderColor={borderColor}>
        <Heading size="md" mb={2}>
          USDC/USDT Fees
        </Heading>
        <Text color="gray.600">
          You haven't set a USDC/USDT fee yet. You can set one in{' '}
          <Link as={RouterLink} to="/settings" color="blue.500" textDecoration="underline">
            Settings
          </Link>
          .
        </Text>
      </Box>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      <Heading size="md">USDC/USDT Fees</Heading>

      {statsError && (
        <Alert status="error" rounded="md" fontSize="sm">
          <AlertIcon />
          {statsError}
        </Alert>
      )}
      {statsHidden && (
        <Alert status="info" rounded="md" fontSize="sm">
          <AlertIcon />
          {fees?.enabled
            ? "Earnings, balances and claims aren't available right now. Please try again later."
            : "Earnings, balances and claims aren't available while the USDC/USDT fee is turned off. Turn it back on in Settings to see them."}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", xl: "repeat(4, 1fr)" }} gap={4}>
        <Box p={4} bg={cardBg} rounded="xl" border="1px" borderColor={borderColor}>
          <HStack spacing={2} color="gray.500" fontSize="xs">
            <Icon as={FiPercent} boxSize={5} color={BRAND} />
            <Text>Current fee</Text>
          </HStack>
          <Skeleton isLoaded={!feesQuery.isLoading} mt={2} maxW="120px">
            <Text fontSize="2xl" fontWeight="bold">
              {fees?.enabled ? `${fees.feePercentage}%` : '–'}
            </Text>
          </Skeleton>
          <Text fontSize="xs" color="gray.400" mt={1}>
            {fees?.enabled ? `Paid out as ${fmtAssetOnChain(fees.payoutAsset, fees.payoutChain)}` : 'Turned off: no new fees are charged'}
          </Text>
        </Box>
        <Box p={4} bg={cardBg} rounded="xl" border="1px" borderColor={borderColor}>
          <HStack spacing={2} color="gray.500" fontSize="xs">
            <Icon as={FiTrendingUp} boxSize={5} color="#10b981" />
            <Text>Fees earned</Text>
          </HStack>
          <Skeleton isLoaded={!statsLoading} mt={2} maxW="180px">
            <Text fontSize="2xl" fontWeight="bold">
              {orNoData(fmtUsd(stableAmount(metrics?.earnedAmount)))}
            </Text>
          </Skeleton>
          <Text fontSize="xs" color="gray.400" mt={1}>
            After Flashnet's share, in the selected period
          </Text>
        </Box>
        <Box p={4} bg={cardBg} rounded="xl" border="1px" borderColor={borderColor}>
          <HStack spacing={2} color="gray.500" fontSize="xs">
            <Icon as={FiDollarSign} boxSize={5} color={BRAND} />
            <Text>Volume</Text>
          </HStack>
          <Skeleton isLoaded={!statsLoading} mt={2} maxW="180px">
            <Text fontSize="2xl" fontWeight="bold">
              {orNoData(fmtUsd(metrics?.volumeUsd))}
            </Text>
          </Skeleton>
          <Text fontSize="xs" color="gray.400" mt={1}>
            Completed payments carrying your fee
          </Text>
        </Box>
        <Box p={4} bg={cardBg} rounded="xl" border="1px" borderColor={borderColor}>
          <HStack spacing={2} color="gray.500" fontSize="xs">
            <Icon as={FiRepeat} boxSize={5} color={BRAND} />
            <Text>Payments</Text>
          </HStack>
          <Skeleton isLoaded={!statsLoading} mt={2} maxW="120px">
            <Text fontSize="2xl" fontWeight="bold">
              {orNoData(fmtNum(metrics?.completedCount || 0))}
            </Text>
          </Skeleton>
          <Text fontSize="xs" color="gray.400" mt={1}>
            {statsMissing ? 'Completed payments' : `Completed, of ${fmtNum(metrics?.orderCount || 0)} started`}
          </Text>
        </Box>
      </Grid>

      {/* Claimable balances */}
      <Box p={4} bg={cardBg} rounded="xl" border="1px" borderColor={borderColor}>
        <Heading size="sm" color="gray.600" mb={1}>
          Balances
        </Heading>
        <Text fontSize="xs" color="gray.500" mb={3}>
          A claim pays out the full balance to the address it was earned under. Claims need at least $1.
        </Text>
        <Skeleton isLoaded={!statsLoading}>
          {/* All-time totals, as on the Orchestra affiliate dashboard */}
          {totals && (
            <Grid templateColumns={{ base: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }} gap={4} mb={4}>
              {[
                { label: 'Available now', amount: totals.availableBalance },
                { label: 'Pending payout', amount: totals.pendingClaimAmount },
                { label: 'Paid out', amount: totals.paidOutAmount },
                { label: 'Lifetime earned', amount: totals.totalEarnedAmount },
              ].map(({ label, amount }) => (
                <Box key={label}>
                  <Text fontSize="xs" color="gray.500">{label}</Text>
                  <Text fontSize="lg" fontWeight="bold">{fmtUsd(stableAmount(amount))}</Text>
                </Box>
              ))}
            </Grid>
          )}
          {statsError ? (
            <Notice error>Balances couldn't be loaded.</Notice>
          ) : statsHidden ? (
            <Notice>
              {fees?.enabled
                ? "Balances aren't available right now."
                : 'Turn the USDC/USDT fee back on in Settings to see and claim any remaining balance.'}
            </Notice>
          ) : balances.length === 0 ? (
            <Notice>No fees to claim yet.</Notice>
          ) : (
            <Box overflowX="auto">
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Earned in</Th>
                    <Th>Pays to</Th>
                    <Th isNumeric>Amount</Th>
                    <Th />
                  </Tr>
                </Thead>
                <Tbody>
                  {balances.map((balance, index) => (
                    <Tr key={index}>
                      <Td whiteSpace="nowrap">{fmtAssetOnChain(balance.settlementAsset, balance.settlementChain)}</Td>
                      <Td whiteSpace="nowrap">
                        <Text>{fmtAssetOnChain(balance.payoutAsset, balance.payoutChain)}</Text>
                        <Text fontSize="xs" color="gray.500" fontFamily="mono" title={balance.payoutAddress}>
                          {shorten(balance.payoutAddress, 8, 6)}
                        </Text>
                        {!balance.isCurrentPayoutDestination && (
                          <Text fontSize="xs" color="orange.500">Earlier payout address</Text>
                        )}
                      </Td>
                      <Td isNumeric fontWeight="medium" whiteSpace="nowrap">
                        {fmtStable(balance.availableBalance, balance.settlementAsset)}
                      </Td>
                      <Td textAlign="right">
                        {balance.claimable ? (
                          <Button
                            size="sm"
                            onClick={() => setClaiming(balance)}
                            isDisabled={claimMutation.isPending || !fees?.enabled}
                          >
                            Claim
                          </Button>
                        ) : (
                          // Orchestra's reason is developer-facing, so it's only a tooltip
                          <Text fontSize="xs" color="gray.500" maxW="200px" ml="auto" title={balance.unclaimableReason || undefined}>
                            {stableAmount(balance.availableBalance) < 1 ? 'Below the $1 minimum' : 'Not claimable yet'}
                          </Text>
                        )}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
          {/* In case Orchestra does report balances for a turned-off fee: they
              still can't be claimed until it's back on */}
          {!statsMissing && !fees?.enabled && balances.length > 0 && (
            <Notice>Turn the USDC/USDT fee back on in Settings to claim these balances.</Notice>
          )}
        </Skeleton>
      </Box>

      {/* Chart */}
      <Box p={4} bg={cardBg} rounded="xl" border="1px" borderColor={borderColor}>
        <Heading size="sm" color="gray.600" mb={3}>
          Monthly fees earned
        </Heading>
        <Skeleton isLoaded={!statsLoading} minH="40px">
          {statsMissing ? (
            <Notice error={!!statsError}>
              {statsError ? "The chart couldn't be loaded." : `Not available ${unavailableWhen}.`}
            </Notice>
          ) : (
          <Box height="300px">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={(value: number) => fmtUsd(value)} width={64} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(value: number) => [fmtUsd(value), 'Fees']} />
                <Line
                  isAnimationActive={false}
                  type="monotone"
                  dataKey="fees"
                  stroke={BRAND}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  connectNulls={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
          )}
        </Skeleton>
      </Box>

      {/* Claims */}
      <Box p={4} bg={cardBg} rounded="xl" border="1px" borderColor={borderColor}>
        <Heading size="sm" color="gray.600" mb={3}>
          Claims
        </Heading>
        <Skeleton isLoaded={!claimsQuery.isLoading}>
          {claimsQuery.isError ? (
            <Notice error>{apiErrorMessage(claimsQuery.error, 'Failed to load claims')}</Notice>
          ) : claimsQuery.data?.available === false ? (
            <Notice>{`Your claim history isn't available ${unavailableWhen}.`}</Notice>
          ) : claims.length === 0 ? (
            <Notice>No claims yet.</Notice>
          ) : (
            <Box overflowX="auto">
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Date</Th>
                    <Th>Paid to</Th>
                    <Th isNumeric>Amount</Th>
                    <Th>Status</Th>
                    <Th>Transaction</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {claims.map((claim) => (
                    <Tr key={claim.id}>
                      <Td whiteSpace="nowrap">{fmtDate(claim.createdAt)}</Td>
                      <Td whiteSpace="nowrap">
                        <Text>{fmtAssetOnChain(claim.payoutAsset, claim.payoutChain)}</Text>
                        <Text fontSize="xs" color="gray.500" fontFamily="mono" title={claim.payoutAddress}>
                          {shorten(claim.payoutAddress, 8, 6)}
                        </Text>
                      </Td>
                      <Td isNumeric fontWeight="medium" whiteSpace="nowrap">
                        {fmtStable(claim.amount, claim.settlementAsset)}
                      </Td>
                      <Td>
                        <Badge colorScheme={CLAIM_STATUS_COLORS[claim.status] || 'gray'} title={claim.errorMessage || undefined}>
                          {claim.status}
                        </Badge>
                      </Td>
                      <Td fontFamily="mono" fontSize="xs" title={claim.txHash || undefined}>
                        {shorten(claim.txHash, 8, 6)}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
          <Pager offset={claimsOffset} nextOffset={claimsQuery.data?.nextOffset} onChange={setClaimsOffset} />
        </Skeleton>
      </Box>

      {/* Payments carrying the fee */}
      <Box p={4} bg={cardBg} rounded="xl" border="1px" borderColor={borderColor}>
        <Heading size="sm" color="gray.600" mb={3}>
          Recent payments
        </Heading>
        <Skeleton isLoaded={!ordersQuery.isLoading}>
          {ordersQuery.isError ? (
            <Notice error>{apiErrorMessage(ordersQuery.error, 'Failed to load payments')}</Notice>
          ) : ordersQuery.data?.available === false ? (
            <Notice>{`Your payments aren't available ${unavailableWhen}.`}</Notice>
          ) : orders.length === 0 ? (
            <Notice>No payments carrying your fee yet.</Notice>
          ) : (
            <Box overflowX="auto">
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Date</Th>
                    <Th>Route</Th>
                    <Th isNumeric>Your fee</Th>
                    <Th>Status</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {orders.map((order) => (
                    <Tr key={order.operationId}>
                      <Td whiteSpace="nowrap">{fmtDate(order.createdAt)}</Td>
                      <Td>
                        {`${fmtAssetOnChain(order.sourceAsset, order.sourceChain)} → ${fmtAssetOnChain(order.destinationAsset, order.destinationChain)}`}
                      </Td>
                      <Td isNumeric fontWeight="medium" whiteSpace="nowrap">
                        {order.amount ? fmtStable(order.amount, order.settlementAsset) : '–'}
                      </Td>
                      <Td>
                        <Badge colorScheme={order.paidAt ? 'green' : 'gray'}>
                          {order.paidAt ? 'earned' : order.operationStatus || 'pending'}
                        </Badge>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
          <Pager offset={ordersOffset} nextOffset={ordersQuery.data?.nextOffset} onChange={setOrdersOffset} />
        </Skeleton>
      </Box>

      {/* Claim confirmation: Orchestra has no confirmation step of its own */}
      <AlertDialog
        isOpen={claiming !== null}
        leastDestructiveRef={cancelRef}
        onClose={() => !claimMutation.isPending && setClaiming(null)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Claim fees
            </AlertDialogHeader>
            <AlertDialogBody>
              {claiming && (
                <VStack align="stretch" spacing={3}>
                  <Text>
                    Claim the full balance of{' '}
                    <Text as="strong">{fmtStable(claiming.availableBalance, claiming.settlementAsset)}</Text>? A claim can't be
                    partial or undone.
                  </Text>
                  <Box>
                    <Text fontSize="sm" color="gray.600">
                      {`It will be paid as ${fmtAssetOnChain(claiming.payoutAsset, claiming.payoutChain)} to:`}
                    </Text>
                    <Text fontSize="sm" fontFamily="mono" wordBreak="break-all">
                      {claiming.payoutAddress}
                    </Text>
                    {!claiming.isCurrentPayoutDestination && (
                      <Text fontSize="sm" color="orange.500" mt={1}>
                        This is an earlier payout address, not your current one.
                      </Text>
                    )}
                  </Box>
                  <Text fontSize="xs" color="gray.500">
                    The amount delivered can be slightly lower because of conversion and payout costs.
                  </Text>
                </VStack>
              )}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setClaiming(null)} isDisabled={claimMutation.isPending}>
                Cancel
              </Button>
              <Button
                ml={3}
                isLoading={claimMutation.isPending}
                onClick={() => claiming && claimMutation.mutate(claiming)}
              >
                Claim
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </VStack>
  );
};

export default CrossChainFees;
